import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
    Coins, Check, Sparkles, History, ArrowRight, Plus, CreditCard, ShieldCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CREDIT_PACKS = [
    { credits: 5, price: 75, popular: false },
    { credits: 10, price: 140, popular: true, savings: 10 }
];

function CreditPackCard({ pack, onSelect, selected }) {
    return (
        <button
            onClick={() => onSelect(pack.credits)}
            className={`relative w-full rounded-2xl border-2 p-6 text-left transition-all ${
                selected 
                    ? 'border-emerald-600 bg-emerald-50 shadow-lg' 
                    : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-md'
            }`}
            data-testid={`credit-pack-${pack.credits}`}
        >
            {pack.popular && (
                <span className="absolute -top-3 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    BEST VALUE
                </span>
            )}
            
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                        selected ? 'bg-emerald-600' : 'bg-amber-100'
                    }`}>
                        <Coins className={`w-7 h-7 ${selected ? 'text-white' : 'text-amber-600'}`} />
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-stone-900">{pack.credits}</p>
                        <p className="text-stone-500 text-sm">credits</p>
                    </div>
                </div>
                
                <div className="text-right">
                    <p className="text-2xl font-bold text-stone-900">£{pack.price}</p>
                    <p className="text-stone-500 text-sm">£{(pack.price / pack.credits).toFixed(2)}/credit</p>
                </div>
            </div>
            
            {pack.savings && (
                <div className="bg-emerald-100 text-emerald-700 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Save £{pack.savings} compared to 5-pack
                </div>
            )}
            
            {selected && (
                <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                    </div>
                </div>
            )}
        </button>
    );
}

export function CreditsPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { token, profile, refreshProfile } = useAuth();
    const [selectedPack, setSelectedPack] = useState(10);
    const [creditHistory, setCreditHistory] = useState([]);
    const [purchasing, setPurchasing] = useState(false);
    const [loading, setLoading] = useState(true);

    // Handle Stripe redirect results
    useEffect(() => {
        const success = searchParams.get('success');
        const canceled = searchParams.get('canceled');
        const subscriptionSuccess = searchParams.get('subscription_success');
        
        if (success === 'true') {
            toast.success('Payment successful! Credits have been added to your account.');
            if (refreshProfile) refreshProfile();
            // Clear URL params
            window.history.replaceState({}, '', '/credits');
        }
        if (subscriptionSuccess === 'true') {
            toast.success('Subscription activated! Your membership credits have been added.');
            if (refreshProfile) refreshProfile();
            window.history.replaceState({}, '', '/credits');
        }
        if (canceled === 'true') {
            toast.error('Payment was canceled. No charges were made.');
            window.history.replaceState({}, '', '/credits');
        }
    }, [searchParams, refreshProfile]);

    useEffect(() => {
        if (token) {
            fetchHistory();
        }
    }, [token]);

    const fetchHistory = async () => {
        try {
            const response = await axios.get(`${API}/member/credit-history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCreditHistory(response.data);
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async () => {
        setPurchasing(true);
        try {
            // Create Stripe checkout session
            const response = await axios.post(
                `${API}/payments/create-checkout-session`,
                { credits: selectedPack },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Redirect to Stripe Checkout
            if (response.data.checkout_url) {
                window.location.href = response.data.checkout_url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Payment error:', error);
            toast.error(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
            setPurchasing(false);
        }
    };

    const selectedPackDetails = CREDIT_PACKS.find(p => p.credits === selectedPack);

    return (
        <div className="min-h-screen bg-stone-50" data-testid="credits-page">
            <PageHeader title="Credits" showBack />
            
            <div className="px-5 py-6">
                {/* Current Balance */}
                <div className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl p-6 text-center mb-8 shadow-lg">
                    <p className="text-amber-900/80 text-sm font-medium mb-1">Your Balance</p>
                    <div className="flex items-center justify-center gap-2">
                        <Coins className="w-8 h-8 text-amber-900" />
                        <span className="text-5xl font-bold text-amber-900">{profile?.credits_balance || 0}</span>
                    </div>
                    <p className="text-amber-800 text-sm mt-2">credits available</p>
                </div>
                
                {/* Purchase Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-bold text-stone-900 mb-4">Purchase Credits</h2>
                    
                    <div className="space-y-4">
                        {CREDIT_PACKS.map((pack) => (
                            <CreditPackCard
                                key={pack.credits}
                                pack={pack}
                                selected={selectedPack === pack.credits}
                                onSelect={setSelectedPack}
                            />
                        ))}
                    </div>
                    
                    <Button
                        onClick={handlePurchase}
                        disabled={purchasing}
                        className="w-full mt-6 h-14 text-lg font-semibold bg-emerald-700 hover:bg-emerald-800 rounded-xl"
                        data-testid="purchase-credits-btn"
                    >
                        {purchasing ? (
                            <span className="flex items-center gap-2">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Redirecting to payment...
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Purchase {selectedPack} Credits for £{selectedPackDetails?.price}
                            </span>
                        )}
                    </Button>
                    
                    <div className="flex items-center justify-center gap-2 mt-3 text-xs text-stone-500">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Secure payment powered by Stripe</span>
                    </div>
                </div>
                
                {/* Credit History */}
                <div className="pb-24">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-stone-900">Credit History</h2>
                        <History className="w-5 h-5 text-stone-400" />
                    </div>
                    
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : creditHistory.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-stone-200 p-6 text-center">
                            <p className="text-stone-500">No credit transactions yet</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-100" data-testid="credit-history">
                            {creditHistory.map((tx) => (
                                <div key={tx.id} className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                            tx.amount > 0 ? 'bg-emerald-100' : 'bg-stone-100'
                                        }`}>
                                            {tx.amount > 0 ? (
                                                <Plus className="w-5 h-5 text-emerald-600" />
                                            ) : (
                                                <ArrowRight className="w-5 h-5 text-stone-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-stone-900 text-sm">{tx.description}</p>
                                            <p className="text-xs text-stone-500">
                                                {new Date(tx.created_at).toLocaleDateString('en-GB', { 
                                                    day: 'numeric', 
                                                    month: 'short',
                                                    year: 'numeric'
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`font-bold text-lg ${
                                        tx.amount > 0 ? 'text-emerald-600' : 'text-stone-600'
                                    }`}>
                                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CreditsPage;
